type AppLanguageSupport = {
  full: string;
  short: string;
  international: string;
  /** Only when the flag differs from the language code. */
  flag?: string;
  /** Detected locale codes that resolve to this language. */
  aliases?: readonly string[];
};

export const APP_LANGUAGE_SUPPORTS = {
  en: {full: 'English', short: 'eng', flag: 'gb', international: 'English'},
  // es: {full: 'Español', short: 'esp', international: 'Spanish'},
  // 'pt-br': {
  //   full: 'Português (Brasil)',
  //   short: 'por',
  //   flag: 'br',
  //   international: 'Portugese',
  //   aliases: ['pt'],
  // },
  // de: {full: 'Deutsch', short: 'deu', international: 'German'},
  // tr: {full: 'Türkçe', short: 'tür', international: 'Turkish'},
  // ja: {full: '日本語', short: '日', flag: 'jp', international: 'Japanese'},
  // id: {full: 'Bahasa Indonesia', short: 'ind', international: 'Indonesian'},
  // fr: {full: 'Français', short: 'fra', international: 'French'},
  // it: {full: 'Italiano', short: 'ita', international: 'Italian'},
  // th: {full: 'ไทย', short: 'ไทย', international: 'Thai'},
  // ca: {full: 'Català', short: 'cat', flag: 'es', international: 'Catalan'},
  // 'zh-hans': {
  //   full: '简体中文',
  //   short: '简',
  //   flag: 'cn',
  //   international: 'Chinese Simplified',
  //   aliases: ['zh', 'zh-cn', 'zh-sg'],
  // },
  // 'zh-hant': {
  //   full: '繁體中文',
  //   short: '繁',
  //   flag: 'tw',
  //   international: 'Chinese Traditional',
  //   aliases: ['zh-tw', 'zh-hk', 'zh-mo'],
  // },
  // cs: {full: 'Čeština', short: 'čes', flag: 'cz', international: 'Czech'},
  // nl: {full: 'Nederlands', short: 'ned', international: 'Dutch'},
  // fil: {
  //   full: 'Filipino',
  //   short: 'fil',
  //   flag: 'ph',
  //   international: 'Filipino',
  //   aliases: ['tl'],
  // },
  // hi: {full: 'हिन्दी', short: 'हिं', flag: 'in', international: 'Hindi'},
  // ko: {full: '한국어', short: '한', flag: 'kr', international: 'Korean'},
  // ms: {full: 'Bahasa Melayu', short: 'mel', flag: 'my', international: 'Malay'},
  // pl: {full: 'Polski', short: 'pol', international: 'Polish'},
  // ro: {full: 'Română', short: 'rom', international: 'Romanian'},
  // ru: {full: 'Русский', short: 'рус', international: 'Russian'},
  // sv: {full: 'Svenska', short: 'sve', flag: 'se', international: 'Swedish'},
  // uk: {
  //   full: 'Українська',
  //   short: 'укр',
  //   flag: 'ua',
  //   international: 'Ukrainian',
  // },
  // vi: {
  //   full: 'Tiếng Việt',
  //   short: 'vie',
  //   flag: 'vn',
  //   international: 'Vietnamese',
  // },
  //
  // nb: {
  //   full: 'Norsk bokmål',
  //   short: 'nor',
  //   flag: 'no',
  //   international: 'Bokmal (Norwegian)',
  //   aliases: ['no', 'nn'],
  // },
  // da: {full: 'Dansk', short: 'dan', flag: 'dk', international: 'Danish'},
  // el: {full: 'Ελληνικά', short: 'ελλ', flag: 'gr', international: 'Greek'},
} as const satisfies Record<string, AppLanguageSupport>;

export type AppLanguage = keyof typeof APP_LANGUAGE_SUPPORTS;

export const APP_LANGUAGE_SUPPORTS_KEYS = Object.keys(APP_LANGUAGE_SUPPORTS);
const APP_LANGUAGE_SUPPORTS_SET = new Set(APP_LANGUAGE_SUPPORTS_KEYS);

const APP_LANGUAGE_ALIASES = new Map<string, AppLanguage>(
  Object.entries<AppLanguageSupport>(APP_LANGUAGE_SUPPORTS).flatMap(
    ([language, {aliases}]) =>
      aliases?.map((alias) => [alias, language as AppLanguage] as const) ?? []
  )
);

export function isAppLanguage(value: string): value is AppLanguage {
  return APP_LANGUAGE_SUPPORTS_SET.has(value);
}

export function getCountyNameByCode(language: AppLanguage) {
  return APP_LANGUAGE_SUPPORTS[language].full;
}

export function getCountyShortNameByCode(language: AppLanguage) {
  return APP_LANGUAGE_SUPPORTS[language].short;
}

export function getCountryFlagCode(language: AppLanguage): string {
  const {flag}: AppLanguageSupport = APP_LANGUAGE_SUPPORTS[language];

  return flag ?? language.split('-')[0];
}

function normalizeDetectedAppLanguageCode(language: string): string {
  const normalizedLanguage = language.toLowerCase();

  if (isAppLanguage(normalizedLanguage)) {
    return normalizedLanguage;
  }

  const aliasedLanguage = APP_LANGUAGE_ALIASES.get(normalizedLanguage);

  if (aliasedLanguage) {
    return aliasedLanguage;
  }

  const [baseLanguage] = normalizedLanguage.split('-');

  return baseLanguage ?? normalizedLanguage;
}

export function resolveSupportedAppLanguage(
  languageCodes: readonly string[]
): AppLanguage | undefined {
  for (const languageCode of languageCodes) {
    const normalizedLanguageCode =
      normalizeDetectedAppLanguageCode(languageCode);

    if (isAppLanguage(normalizedLanguageCode)) {
      return normalizedLanguageCode;
    }
  }
}

export function isAppSupportMultiLanguages() {
  return Object.keys(APP_LANGUAGE_SUPPORTS).length > 1;
}
